import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { YearlyPlan } from "../../mock/PricingData";

const fallbackMemberships = {
  "bronze": { id: 101, annuallyCost: 0, monthlyCost: 0 },
  "copper": { id: 102, annuallyCost: 29.99, monthlyCost: 2.99 },
  "silver": { id: 103, annuallyCost: 99.99, monthlyCost: 9.99 },
  "gold": { id: 104, annuallyCost: 36000, monthlyCost: 3600 },
  "platinum": { id: 105, annuallyCost: 54000, monthlyCost: 5400 },
  "titanium": { id: 106, annuallyCost: 72000, monthlyCost: 7200 },
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const BASE_URL = `${process.env.REACT_APP_BASE_URL}`;

  // Get selected plan info from router state, default to Gold Yearly
  const stateData = location.state || {};
  const planName = stateData.planName || "Gold";
  const billingCycle = stateData.billingCycle || "Yearly";

  const [selectedMembership, setSelectedMembership] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states pre-filled with logged-in user details if available
  const [nameInput, setNameInput] = useState(localStorage.getItem("name") || "");
  const [emailInput, setEmailInput] = useState(localStorage.getItem("email") || "");
  const [phoneInput, setPhoneInput] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [countryInput, setCountryInput] = useState("United States");
  const [discountCode, setDiscountCode] = useState(stateData.discountCode || "");

  // Fetch real memberships from database on load
  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await axios.get(`${BASE_URL}api/get-all-membership-subscription`);
        if (response.data && response.data.length > 0) {
          const matched = response.data.find(
            m => m.MembershipType.toLowerCase() === planName.toLowerCase()
          );
          if (matched) {
            setSelectedMembership(matched);
          }
        }
      } catch (error) {
        console.error("Error fetching memberships from server:", error);
      }
    };
    fetchMemberships();
  }, [BASE_URL, planName]);

  // Find corresponding mock plan details to render facilities and icon
  const matchedPlanMock = YearlyPlan.find(
    p => p.plan.toLowerCase() === planName.toLowerCase()
  ) || YearlyPlan[3]; // Fallback to Gold

  const displayPrice = billingCycle === "Yearly" ? matchedPlanMock.yearprice : matchedPlanMock.monthprice;
  const displayFacilities = billingCycle === "Yearly" ? matchedPlanMock.yearfacilities : matchedPlanMock.monthfacilities;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("accessToken");

    if (!userId || !token) {
      toast.error("Please login to proceed with the checkout.");
      navigate("/login");
      return;
    }

    // Determine target membership ID and amounts
    const mId = selectedMembership?.MembershipId || fallbackMemberships[planName.toLowerCase()]?.id || 104;
    const mType = selectedMembership?.MembershipType || planName;
    const cost = billingCycle === "Yearly"
      ? (selectedMembership?.AnnuallyCost || fallbackMemberships[planName.toLowerCase()]?.annuallyCost || 36000)
      : (selectedMembership?.MonthlyCost || fallbackMemberships[planName.toLowerCase()]?.monthlyCost || 3600);

    const orderTableData = {
      membership: {
        membershipId: parseInt(mId),
        membershipType: mType
      },
      currency: "USD",
      amount: parseFloat(cost),
      isConfirmed: "true",
      user: {
        userId: parseInt(userId)
      },
      email: emailInput,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
      isUpdate: false,
      discountCode: discountCode.trim() || null
    };

    try {
      const response = await axios.post(`${BASE_URL}stripe/payment`, orderTableData);
      
      let responseData = response.data;
      if (typeof responseData === 'string') {
        responseData = JSON.parse(responseData);
      }

      if (responseData.status === "success" && responseData.url) {
        // Redirect to Stripe hosted checkout page
        window.location.href = responseData.url;
      } else {
        toast.error(responseData.message || "Failed to initiate payment session.");
      }
    } catch (error) {
      console.error("Error creating payment session:", error);
      toast.error(error.response?.data?.message || "Something went wrong during checkout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-gray-100 px-10 min-h-screen rounded-lg">
        <div className="flex items-center gap-10">
          <div className="w-1/2 px-20 py-10">
            <form onSubmit={handleSubmit}>
              <div className="space-y-12">
                <div className="border-b border-gray-900/10 pb-12">
                  <h2 className="text-base font-semibold leading-7 text-gray-900 text-center">
                    Checkout
                  </h2>

                  <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label
                        htmlFor="first-name"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Name
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="first-name"
                          id="first-name"
                          value={nameInput}
                          onChange={(e) => setNameInput(e.target.value)}
                          required
                          autoComplete="given-name"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label
                        htmlFor="last-name"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Email
                      </label>
                      <div className="mt-2">
                        <input
                          type="email"
                          name="last-name"
                          id="last-name"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          required
                          autoComplete="email"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Phone
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="phone"
                          id="phone"
                          value={phoneInput}
                          onChange={(e) => setPhoneInput(e.target.value)}
                          autoComplete="tel"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label
                        htmlFor="country"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Country
                      </label>
                      <div className="mt-2">
                        <select
                          id="country"
                          name="country"
                          value={countryInput}
                          onChange={(e) => setCountryInput(e.target.value)}
                          autoComplete="country-name"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        >
                          <option>United States</option>
                          <option>Canada</option>
                          <option>Mexico</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-span-full">
                      <label
                        htmlFor="street-address"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Street address
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="street-address"
                          id="street-address"
                          value={streetAddress}
                          onChange={(e) => setStreetAddress(e.target.value)}
                          autoComplete="street-address"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2 sm:col-start-1">
                      <label
                        htmlFor="city"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        City
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="city"
                          id="city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          autoComplete="address-level2"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="region"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        State / Province
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="region"
                          id="region"
                          value={stateProv}
                          onChange={(e) => setStateProv(e.target.value)}
                          autoComplete="address-level1"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="postal-code"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        ZIP / Postal code
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="postal-code"
                          id="postal-code"
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                          autoComplete="postal-code"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <label
                        htmlFor="discount-code"
                        className="block text-sm font-medium leading-6 text-gray-950 font-semibold"
                      >
                        Discount Code (Optional)
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="discount-code"
                          id="discount-code"
                          placeholder="e.g. BALARCH2026"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value)}
                          className="block w-full rounded-md border border-gray-300 py-1.5 px-3 text-gray-900 shadow-sm focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-center gap-x-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-md w-full bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  {loading ? "Redirecting to Stripe..." : "Checkout"}
                </button>
              </div>
            </form>
          </div>
          <div className="w-1/2">
            <div className="flex flex-col p-6 mx-auto max-w-lg text-center text-gray-900 bg-white rounded-lg border border-gray-100 shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white">
              <h3 className="mb-4 text-2xl font-semibold flex items-center gap-2 justify-center">
                {matchedPlanMock.icon && <img src={matchedPlanMock.icon} alt={planName} />} {planName}
              </h3>
              <p className="font-semibold text-gray-800 sm:text-lg dark:text-gray-400">
                ${displayPrice} per {billingCycle.toLowerCase()}
              </p>
              <p className="font-light text-gray-500 sm:text-lg dark:text-gray-400">
                Billed {billingCycle.toLowerCase()}
              </p>

              <ul className="mb-8 space-y-4 text-left mt-5">
                {displayFacilities.map((facility, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-green-500 dark:text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span>{facility}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
